import { Service } from '@toeverything/infra';

import type { ServerService } from './server';

/**
 * ConfirmationEvent - Typed event for AI confirmation requests
 */
export interface ConfirmationEventDetail {
  id: string;
  toolName: string;
  displayName: string;
  description: string;
  confirmationCopy?: {
    key: string;
    params?: Record<string, string | number | boolean | null>;
  };
  metadata?: Record<string, unknown>;
}

/**
 * sl-canvas2-8.31 Phase 2: Awaiting approval event for Two-Call Pattern
 * Replaces the old stream-paused event
 */
export interface AwaitingApprovalEventDetail {
  approvalId: string;
  toolName: string;
  arguments: Record<string, unknown>;
}

/**
 * @deprecated Use AwaitingApprovalEventDetail instead
 */
export interface StreamPausedEventDetail {
  reason: string;
  confirmationId?: string;
}

/**
 * Singleton event target for confirmation events.
 * Uses browser's native EventTarget API for proper cleanup.
 */
class ConfirmationEventBus extends EventTarget {
  private static instance: ConfirmationEventBus;

  static getInstance(): ConfirmationEventBus {
    if (!ConfirmationEventBus.instance) {
      ConfirmationEventBus.instance = new ConfirmationEventBus();
    }
    return ConfirmationEventBus.instance;
  }

  emit(detail: ConfirmationEventDetail): void {
    this.dispatchEvent(new CustomEvent('confirmation-required', { detail }));
  }

  subscribe(handler: (detail: ConfirmationEventDetail) => void): () => void {
    const listener = (event: Event) => {
      handler((event as CustomEvent<ConfirmationEventDetail>).detail);
    };
    this.addEventListener('confirmation-required', listener);
    return () => this.removeEventListener('confirmation-required', listener);
  }
}

// Export singleton instance
export const confirmationEventBus = ConfirmationEventBus.getInstance();

/**
 * sl-canvas2-8.31: Event bus for stream-paused events
 */
class StreamPausedEventBus extends EventTarget {
  private static instance: StreamPausedEventBus;

  static getInstance(): StreamPausedEventBus {
    if (!StreamPausedEventBus.instance) {
      StreamPausedEventBus.instance = new StreamPausedEventBus();
    }
    return StreamPausedEventBus.instance;
  }

  emit(detail: StreamPausedEventDetail): void {
    this.dispatchEvent(new CustomEvent('stream-paused', { detail }));
  }

  subscribe(handler: (detail: StreamPausedEventDetail) => void): () => void {
    const listener = (event: Event) => {
      handler((event as CustomEvent<StreamPausedEventDetail>).detail);
    };
    this.addEventListener('stream-paused', listener);
    return () => this.removeEventListener('stream-paused', listener);
  }
}

export const streamPausedEventBus = StreamPausedEventBus.getInstance();

/**
 * sl-canvas2-8.31: Event bus for confirmation-completed events
 * Used to notify chat components to auto-continue after tool execution
 */
export interface ConfirmationCompletedEventDetail {
  sessionId: string;
  success: boolean;
  toolResult?: {
    success: boolean;
    data?: unknown;
    result?: unknown;
    error?: string;
    hint?: string;
  };
}

class ConfirmationCompletedEventBus extends EventTarget {
  private static instance: ConfirmationCompletedEventBus;

  static getInstance(): ConfirmationCompletedEventBus {
    if (!ConfirmationCompletedEventBus.instance) {
      ConfirmationCompletedEventBus.instance =
        new ConfirmationCompletedEventBus();
    }
    return ConfirmationCompletedEventBus.instance;
  }

  emit(detail: ConfirmationCompletedEventDetail): void {
    console.log('[ConfirmationCompletedEventBus] Emitting:', detail);
    this.dispatchEvent(new CustomEvent('confirmation-completed', { detail }));
  }

  subscribe(
    handler: (detail: ConfirmationCompletedEventDetail) => void
  ): () => void {
    const listener = (event: Event) => {
      handler((event as CustomEvent<ConfirmationCompletedEventDetail>).detail);
    };
    this.addEventListener('confirmation-completed', listener);
    return () => this.removeEventListener('confirmation-completed', listener);
  }
}

export const confirmationCompletedEventBus =
  ConfirmationCompletedEventBus.getInstance();

export class EventSourceService extends Service {
  constructor(private readonly serverService: ServerService) {
    super();
  }

  eventSource = (url: string, eventSourceInitDict?: EventSourceInit) => {
    const fullUrl = new URL(url, this.serverService.server.baseUrl);
    const eventSource = new EventSource(fullUrl, eventSourceInitDict);

    // Only intercept copilot chat streams for confirmation events
    if (fullUrl.pathname.includes('/api/copilot/chat/')) {
      eventSource.addEventListener(
        'confirmation-required',
        (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data);

            // Emit via event bus - no blocking, immediate dispatch
            confirmationEventBus.emit({
              id: data.id,
              toolName: data.toolName,
              displayName: data.displayName || data.toolName,
              description: data.description,
              confirmationCopy: data.confirmationCopy,
              metadata: data.metadata,
            });
          } catch (error) {
            console.error(
              '[EventSourceService] Failed to parse confirmation-required event:',
              error
            );
          }
        }
      );

      // sl-canvas2-8.31 Phase 2: Listen for awaiting-approval event (Two-Call Pattern)
      // This is the new event that replaces stream-paused
      eventSource.addEventListener(
        'awaiting-approval',
        (event: MessageEvent) => {
          try {
            const data = JSON.parse(event.data);
            console.log(
              '[EventSourceService] Awaiting approval (Phase 2):',
              data
            );

            // sl-55fc.4: displayName as string from Backend SSoT
            confirmationEventBus.emit({
              id: data.approvalId,
              toolName: data.toolName,
              displayName: data.displayName || data.toolName,
              description:
                data.description ||
                `Execute ${data.displayName || data.toolName}?`,
              confirmationCopy: data.confirmationCopy,
              metadata: {
                arguments: data.arguments,
                ...(typeof data.sessionId === 'string'
                  ? { sessionId: data.sessionId }
                  : {}),
              },
            });
          } catch (error) {
            console.error(
              '[EventSourceService] Failed to parse awaiting-approval event:',
              error
            );
          }
        }
      );

      // @deprecated: Keep stream-paused for backwards compatibility
      eventSource.addEventListener('stream-paused', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[EventSourceService] Stream paused (legacy):', data);

          // Emit via event bus
          streamPausedEventBus.emit({
            reason: data.reason,
            confirmationId: data.confirmationId,
          });
        } catch (error) {
          console.error(
            '[EventSourceService] Failed to parse stream-paused event:',
            error
          );
        }
      });
    }

    return eventSource;
  };
}
